/**
 * Defines an action to be performed in the UI, including any parameters required to execute the action.
 */

import type { ConditionEvaluationObject } from '../conditionEvaluationObject';

export enum ActionType {
  ArchiveRecord = 'archiveRecord',
  ResyncRecord = 'resyncRecord',
  CloneRecord = 'cloneRecord',
  OnRowSelect = 'onRowSelect',
  /** Invokes a registered control method on a target block (e.g. open, submit, focus). */
  ControlBlockMethod = 'controlBlockMethod',
  /** Opens, closes, or toggles a bottom sheet block. */
  ControlBottomSheet = 'controlBottomSheet',
  ControlBuild = 'controlBuild',
  /** Triggers or controls a bound data source (e.g. manual fetch / refetch). */
  ControlDataSource = 'controlDataSource',
  ControlDevicePermissions = 'controlDevicePermissions',
  /** Opens, closes, or toggles a drawer block. */
  ControlDrawer = 'controlDrawer',
  ControlDrawerNavigator = 'controlDrawerNavigator',
  /** Controls media/file upload flow on a target upload block. */
  ControlMediaUploads = 'controlMediaUploads',
  ControlMediaViewer = 'controlMediaViewer',
  /** Opens, closes, or toggles a modal block. */
  ControlModal = 'controlModal',
  /** Opens, closes, or toggles the notifications UI / notification panel. */
  ControlNotification = 'controlNotification',
  ControlOfflineMode = 'controlOfflineMode',
  ControlStep = 'controlStep',
  /** Runs user-session actions: preferences, logout, locale, theme, auth refresh, SSO, etc. */
  ControlUser = 'controlUser',
  /** Runs user-location actions: live-location tracking + one-shot geolocation update */
  ControlUserLocation = 'controlUserLocation',
  ControlUAEPass = 'controlUAEPass',
  ControlRazorpay = 'controlRazorpay',
  ControlCleverTap = 'controlCleverTap',
  ControlYellowAI = 'controlYellowAI',
  ControlWalkthrough = 'controlWalkthrough',
  /** Starts or stops block-selection mode; emits OnComponentSelected with the picked block info and currenly hovered block info */
  ControlBlockSelection = 'controlBlockSelection',
  /** Copies configured text to the system clipboard. */
  CopyToClipboard = 'copyToClipboard',
  CreateRecord = 'createRecord',
  DeleteRecord = 'deleteRecord',
  /** Downloads a file from a URL or configured source. */
  DownloadFile = 'downloadFile',
  EditPartialRecord = 'editPartialRecord',
  EditRecord = 'editRecord',
  /** Emits a named page event for listeners on the current page. */
  EmitPageEvent = 'emitPageEvent',
  EvalJS = 'evalJS',
  /** Runs a configured script (sandboxed / app-defined execution). */
  ExecuteScript = 'executeScript',
  LogToDebugger = 'logToDebugger',
  /** Exports table or grid data (e.g. CSV) per block configuration. */
  ExportData = 'exportData',
  ExportRecord = 'exportRecord',
  ExportRecords = 'exportRecords',
  GenerateToken = 'generateToken',
  ImportAPISpec = 'importAPISpec',
  ImportObjects = 'importObjects',
  Login = 'login',
  ManageBiometrics = 'manageBiometrics',
  /** On-device utilities with no web equivalent. Currently: preloading media into the image cache. */
  MobileUtils = 'mobileUtils',
  /** Navigates to a URL or path via the app router (and linking on native). */
  Navigate = 'navigate',
  /** Goes back in navigation history (browser or in-app stack). */
  NavigateBack = 'navigateBack',
  /** Navigates to another interface page by page id / slug. */
  NavigateToPage = 'navigateToPage',
  /** Opens the platform share sheet for text, files, or URLs. */
  OpenShareDialog = 'openShareDialog',
  PrefetchRecord = 'prefetchRecord',
  Refetch = 'refetch',
  RenameRecord = 'renameRecord',
  Reorder = 'reorder',
  RestoreRecord = 'restoreRecord',
  RestoreVersion = 'restoreVersion',
  /** Dispatches a custom app event on the event bus. */
  SendCustomEvent = 'sendCustomEvent',
  /** Publishes a message to an MQTT topic per payload. */
  SendMQTTEvent = 'sendMQTTEvent',
  SetGovernance = 'setGovernance',
  /** Writes a value to interface-scoped session storage. */
  SetInterfaceSessionStorage = 'setInterfaceSessionStorage',
  /** Writes a value to browser localStorage under a configured key. */
  SetLocalStorage = 'setLocalStorage',
  /** Sets a page- or global-page variable used in bindings and conditions. */
  SetPageVariable = 'setPageVariable',
  SetTheme = 'setTheme',
  /** Updates the current URL query or hash parameters without a full navigation. */
  SetURLParameters = 'setURLParameters',
  ShareEntityRecord = 'shareEntityRecord',
  ShareRecord = 'shareRecord',
  /** Shows a snackbar / toast notification to the user. */
  ShowNotification = 'showNotification',
  /** Submits a form block (validation + submit handler). */
  SubmitForm = 'submitForm',
  /** Invokes a configured backend / serverless function. */
  TriggerFunction = 'triggerFunction',
  ViewRecord = 'viewRecord',
  ControlPushNotifications = 'controlPushNotifications',
  RedirectToStartPage = 'redirectToStartPage',
  /* Repeats a run from the runs table row action */
  RepeatRun = 'repeatRun',
}

export type ExecutionType = 'delay' | 'debounce' | 'throttle';

/**
 * Terminal-form action allowed inside another action's `onSuccessActions` /
 * `onErrorActions`. Callback chains are at most one level deep — the runtime
 * validator in `llm-tools/src/tools/standardSchema.ts` enforces the same
 * constraint, this type mirrors it at compile time.
 */
export type CallbackAction = Omit<Action, 'onSuccessActions' | 'onErrorActions'>;

export interface Action {
  // should be mandatory but not done because of backward compatibility
  id?: string;
  /**
   * The type or name of the action to be performed. This should correspond to a predefined set of actions in the application.
   */
  actionType?: ActionType;

  /**
   * The label to display for the action in the UI.
   */
  label?: string;
  /**
   * The name of the icon to display for the action in the UI. This should correspond to a predefined set of icons in the application.
   */
  icon?: string;
  /**
   * A brief description of what the action does, optional but helpful for understanding the action's purpose.
   */
  description?: string;
  /**
   * A message to display to the user to confirm the action before it is executed. This is useful for potentially destructive actions.
   */
  confirmation?: string;
  /**
   * Whether the action should be visible or not.
   */
  visible?: boolean;
  /**
   * The variant of UI element that is used in action
   */
  variant?: string;
  /**
   * Whether the action should be disabled or not.
   */
  disabled?: boolean;
  /**
   * An object containing additional data to be passed to the action handler. This can be dynamically defined based on the actionType.
   */
  payload?: {
    /**
     * The type of object to which the action applies. This should correspond to a predefined set of object types in the application.
     */
    objectType?: string;
    /**
     * The ID of the record to which the action applies.
     */
    recordId?: string;
    [k: string]: unknown;
  };
  /**
   * The context of the action.
   */
  actionContext?: {
    [key: string]: unknown;
    /**
     * The current row of the table.
     */
    currentRow?: Record<string, unknown>;
  };

  enabled?: string | boolean;

  onSuccessActions?: CallbackAction[];
  onErrorActions?: CallbackAction[];
  /**
   * The condition under which the action should be triggered.
   */
  runCondition?: ConditionEvaluationObject;
  /**
   * The delay in milliseconds before the action is triggered.
   * Used by all execution strategies (delay, debounce, throttle).
   */
  delayDuration?: number;
  /**
   * Execution strategy for the action. Defaults to 'delay' if not present.
   * - "delay": waits delayDuration ms before executing, each trigger independent
   * - "debounce": resets timer on each trigger, executes after triggers stop for delayDuration ms
   * - "throttle": executes at most once per delayDuration ms window
   */
  executionType?: ExecutionType;
  /**
   * For timer block, to run event each time after a specific time interval
   */
  timeInterval?: number;
}

export enum CallbackActionType {
  SUCCESS = 'success',
  FAILURE = 'failure',
}
